import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'env/environment';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ClassifierByIdCard,
  ClassifierCard,
  ClassifierListItem,
  ClassifierValueByIdCard,
  CreateClassifierValueRequest,
  UpdateClassifierRequest,
  UpdateClassifierValueRequest
} from 'types/classifier.interface';
import {
  ClientCard,
  ClientListItemPagedResponse,
  ClientLoginListByLegalEntityIdItem,
  ClientLoginListItem,
  ClientNoteCard,
  ClientNoteListByClientIdItem,
  ClientNotificationByClientIdListItemPagedResponse,
  ClientNotificationCard,
  CreateClientNoteRequest,
  CreateClientNotificationRequest,
  CreateClientRequest,
  CreateReaderCardRequest,
  CreateResearchTopicRequest,
  DeleteClientRequest,
  ReadClientNotificationRequest,
  ReaderCardByClientId,
  ReaderCardById,
  ResearchTopicByClientIdCardPagedResponse,
  ResearchTopicByIdCard,
  ResearchTopicListItemPagedResponse,
  UpdateClientNoteRequest,
  UpdateClientNotificationRequest,
  UpdateClientRequest,
  UpdateReaderCardRequest,
  UpdateResearchTopicActivityRequest,
  UpdateResearchTopicRequest
} from 'types/client.interface';
import { CountryDtoPagedResult } from 'types/country.interface';
import {
  CreateLegalEntityNoteRequest,
  CreateLegalEntityRequest,
  LegalEntityCard,
  LegalEntityFormOption,
  LegalEntityContactInfo,
  LegalEntityListItemPagedResponse,
  LegalEntityNoteCard,
  LegalEntityNoteListItem,
  UpdateLegalEntityNoteRequest,
  UpdateLegalEntityRequest
} from 'types/legal-entity.interface';
import { FILE_TERM_TYPE_FIXED, FILE_TERM_TYPE_UNKNOWN, UpdateFileNoteRequest } from 'types/file.interface';
import {
  CreateReadingRoomRequest,
  ReadingRoomCard,
  ReadingRoomListItemPagedResponse,
  ReadingRoomMinimalListItem,
  UpdateReadingRoomRequest
} from 'types/reading-room.interface';
import { RoleCard, RoleListItemPagedResponse, StructuralUnitRoleListItemPagedResponse, UpdateRoleRequest } from 'types/role.interface';
import {
  AttendReadingRoomSubmissionRequestDto,
  BackofficeReadingRoomSubmissionCard,
  BackofficeReadingRoomSubmissionListItemPagedResponse,
  CreateReadingRoomSubmissionLogAdditionalInfoDto,
  CreateReadingRoomSubmissionNoteRequestDto,
  CreateReadingRoomSubmissionRequestAdditionalInfoDto,
  InitializeBackofficeReadingRoomSubmissionRequest,
  ReadingRoomSubmissionChangeListItem,
  ReadingRoomSubmissionCommunicationDto,
  ReadingRoomSubmissionCommunicationListItemDtoPagedResult,
  ReadingRoomSubmissionNoteCardDto,
  ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto,
  ReassignReadingRoomSubmissionRequest,
  RedirectReadingRoomSubmissionRequest,
  RescheduleReadingRoomSubmissionRequest,
  SetRestrictedAccessJustificationRequest,
  UpdateDraftReadingRoomSubmissionRequest,
  UpdateDraftReadingRoomSubmissionRequestedDocumentsInformationRequest,
  UpdateDraftReadingRoomSubmissionResearchTopicsRequest,
  UpdateLogAdditionalInformationForReadingRoomSubmissionRequest,
  UpdateReadingRoomSubmissionNoteRequestDto,
  UpdateRequestedDocumentBackofficeNotesRequest
} from 'types/service.interface';
import {
  StructuralUnitCard,
  StructuralUnitListItemPagedResponse,
  StructuralUnitMinimalListItem,
  UpdateStructuralUnitRequest
} from 'types/structural-unit.interface';
import {
  CreateUserAbsenceRequest,
  CreateUserNotificationRequest,
  UpdateUserAbsenceRequest,
  UpdateUserNotificationIsReadRequest,
  UpdateUserNotificationRequest,
  UpdateUserRequest,
  UserAbsenceCard,
  UserAbsenceListItem,
  UserCard,
  UserListItemPagedResponse,
  UserMinimalListItem,
  UserNotificationCard,
  UserNotificationListByUserIdItemPagedResponse,
  UserNotificationListItem
} from 'types/user.interface';
import {
  InformationRequestCard,
  InformationRequestTopicResponse,
  InformationRequestLabelsResponse,
  Language,
  CreateBoInformationRequestRequest,
  UpdateBoInformationRequestRequest,
  CompleteInformationRequestRequest,
  CancelBoInformationRequestRequest,
  ResponseDocumentTypeItemRequest,
  NamejsDocumentKind,
  RecordSendingEventRequest,
  SendResponseToPortalRequest,
  InformationRequestNoteCard,
  InformationRequestNoteListByInformationRequestIdItem,
  CreateInformationRequestNoteRequest,
  UpdateInformationRequestNoteRequest,
  BackofficeInformationRequestCommunicationListItemPagedResponse,
  BackofficeInformationRequestCommunicationCard,
  RequestAdditionalInformationForInformationRequestRequest,
  LogAdditionalInformationForInformationRequestRequest,
  UpdateLogAdditionalInformationForInformationRequestRequest,
  SendByEmailForInformationRequestRequest,
  SendResponseByEmailForInformationRequestRequest,
  ForwardInformationRequestRequest,
  HandOverInformationRequestRequest,
  CreateInformationRequestTaskRequest,
  InformationRequestTaskCard,
  InformationRequestTaskListResult,
  CancelInformationRequestTaskRequest,
  InformationRequestCalculationCard,
  InformationRequestCalculationListByInformationRequestIdItem,
  CreateInformationRequestCalculationRequest,
  UpdateInformationRequestCalculationRequest,
  InformationRequestInvoiceCard,
  InformationRequestInvoiceListByInformationRequestIdItem,
  CreateInformationRequestInvoiceRequest,
  UpdateInformationRequestInvoiceRequest,
  SendInformationRequestInvoiceByEmailRequest,
  SendInformationRequestInvoiceToPortalRequest,
  MarkInformationRequestInvoiceSentRequest,
  UpdateInformationRequestPaymentTypesRequest,
  MarkInformationRequestAsPaidRequest,
  InformationRequestChangeListItem,
  AddCoResponsibleStructuralUnitRequest,
  SetConsolidatedRequest,
  BackofficeInformationRequestCard,
  InformationRequestDocumentItem
} from 'types/information-request.interface';
import { AuthorizationService } from './authorization.service';
import { TooltipListItem, UpdateTooltipRequest } from 'types/tooltip.interface';
import { VersionResponse } from 'types/version.interface';

export const GET_ALL_PAGE_SIZE = 10000;
export const DEFAULT_NOTES_PAGE_SIZE = 5;

// Classifier endpoints
export const API_CLASSIFIERS = '/api/classifier';
export const API_CLASSIFIER_ALL_VALUES = '/api/classifier/{code}/all-values';
export const API_CLASSIFIER_VALUES = '/api/classifier/values';

// Structural unit endpoints
export const API_STRUCTURAL_UNIT = '/api/structural-unit';

// Reading room endpoints
export const API_READING_ROOM = '/api/reading-room';
export const API_READING_ROOM_EXCEL = '/api/reading-room/export/excel';
export const API_VISIT_LIST = '/api/reading-room-submission';
export const API_VISIT_ROOM_INITIALIZE = '/api/reading-room-submission/initialize';
export const API_VISIT_ROOM = '/api/reading-room-submission';
export const API_VISIT_ROOM_CHANGES = '/api/reading-room-submission/{id}/changes';
export const API_VISIT_ROOM_SET_TOPIC = '/api/reading-room-submission/update-draft-research-topics';
export const API_VISIT_ROOM_SET_DOCS = '/api/reading-room-submission/update-draft-requested-documents-info';
export const API_VISIT_ROOM_SUBMIT = '/api/reading-room-submission/submit-draft';
export const API_VISIT_ROOM_INFO_DRAFT = '/api/reading-room-submission/update-draft';
export const API_VISIT_ROOM_SUBMISSION_EXCEL = '/api/reading-room-submission/export/excel';
export const API_VISIT_ROOM_SUBMISSION_COMMUNICATION = '/api/reading-room-submission/communication-list';
export const API_VISIT_ROOM_SUBMISSION_COMMUNICATION_READ = '/api/reading-room-submission/read-communication';
export const API_READING_ROOM_SUBMISSION_COMMUNICATION_LOG_INFO = '/api/reading-room-submission/update-log-info';
export const API_READING_ROOM_SUBMISSION_REASSIGN = '/api/reading-room-submission/reassign';
export const API_READING_ROOM_SUBMISSION_NOTE = '/api/reading-room-submission-note';
export const API_READING_ROOM_SUBMISSION_NOTE_READING_ROOM_SUBMISSION = '/api/reading-room-submission-note/reading-room-submission';
export const API_VISIT_ROOM_DOCUMENT_NOTES = '/api/reading-room-submission/update-draft-requested-documents-info';
export const API_VISIT_ROOM_DOCUMENT_BACKOFFICE_NOTES = '/api/reading-room-submission/update-draft-requested-documents-backoffice-notes';
export const API_VISIT_ROOM_AGREE_TO_TERMS = '/api/reading-room-submission/accept-terms';
export const API_VISIT_ROOM_DOCUMENT_EXCEL = '/api/reading-room-submission/export_requested_documents/excel';
export const API_VISIT_ROOM_RESTRICTED_ACCESS_JUSTIFICATION = '/api/reading-room-submission/set-restricted-access-justification';
// Role endpoints
export const API_ROLE = '/api/role';
export const API_ROLE_STRUCTURAL_UNIT = '/api/role/structural-unit';

// Version endpoint
export const API_VERSION = '/api/version';

// User endpoints
export const API_BACKOFFICE_USER = '/api/user';
export const API_USER_ABSENCE = '/api/user-absence/absences-my';
export const API_ALL_ABSENCES = '/api/user-absence/absences-all';
export const API_ABSENCES = '/api/user-absence';
export const API_USER_EXCEL = '/api/user/export/excel';

// Client endpoints
export const API_CLIENT = '/api/client';
export const API_LEGAL_ENTITY = '/api/legal-entity';
export const API_LEGAL_ENTITY_FORMS = '/api/legal-entity/forms';
export const API_COUNTRY = '/api/country';
export const API_RESEARCH_TOPIC = '/api/research-topic';
export const API_CLIENT_NOTIFICATIONS = '/api/client-notification/client';
export const API_CLIENT_NOTIFICATION = '/api/client-notification';
export const API_CLIENT_NOTE = '/api/client-note';
export const API_LEGAL_ENTITY_NOTE = '/api/legal-entity-note';
export const API_READERS_CARD = '/api/reader-card';
export const API_CLIENT_READERS_CARD = '/api/reader-card/client';
export const API_USER_NOTIFICATION = '/api/user-notification';
export const API_CLIENT_LOGIN_LIST = '/api/client-login';

// Information request endpoints
export const API_INFORMATION_REQUEST = '/api/information-request';
export const API_INFORMATION_REQUEST_EXCEL = '/api/information-request/export/excel';
export const API_INFORMATION_REQUEST_TASK = '/api/information-request-task';
export const API_INFORMATION_REQUEST_TASK_EXCEL = '/api/information-request-task/export/excel';
export const API_INFORMATION_REQUEST_NOTE = '/api/information-request-note';
export const API_INFORMATION_REQUEST_CALCULATION = '/api/information-request-calculation';
export const API_INFORMATION_REQUEST_INVOICE = '/api/information-request-invoice';
export const API_SERVICE_REQUEST = '/api/service-request';

// File endpoints
export const API_DOWNLOAD_FILE = '/api/file/download';
export const API_UPLOAD_FILE = '/api/file/upload';
export const API_FILE = '/api/file';

// Auth endpoints
export const API_AUTH_URL = '/api/wsfed-login-url';

// Tooltips
export const API_TOOLTIP = '/api/tooltip';
export const API_AUDIT_LIST = '/api/audit/entries';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  headers: any;

  private notificationsReadChangeSubject = new Subject<UserNotificationListItem>();
  notificationsReadChange$: Observable<UserNotificationListItem> = this.notificationsReadChangeSubject.asObservable();

  getNotificationReadObservable(): Observable<UserNotificationListItem> {
    return this.notificationsReadChange$;
  }

  constructor(
    private http: HttpClient,
    private authService: AuthorizationService
  ) {
    this.headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.getBearerToken()}`
    });
  }

  // Classifier endpoints
  getAllClassifiers(searchTerm: string): Observable<ClassifierListItem[]> {
    const params = `?searchTerm=${searchTerm}`;
    return this.http.get<ClassifierListItem[]>(`${environment.apiUrl}${API_CLASSIFIERS}/${params}`, {
      headers: this.headers
    });
  }

  getVersion() {
    return this.http.get<VersionResponse>(`${environment.apiUrl}${API_VERSION}`, {
      headers: this.headers
    });
  }

  getClassifierValues(code: string): Observable<ClassifierCard> {
    return this.http.get<ClassifierCard>(`${environment.apiUrl}${API_CLASSIFIER_ALL_VALUES.replace('{code}', code)}`, {
      headers: this.headers
    });
  }

  editClassifier(classifier: UpdateClassifierRequest): Observable<ClassifierByIdCard> {
    const body: UpdateClassifierRequest = {
      name: classifier.name,
      code: classifier.code,
      id: classifier.id,
      context: classifier.context
    };
    return this.http.put<ClassifierByIdCard>(`${environment.apiUrl}${API_CLASSIFIERS}`, body, {
      headers: this.headers
    });
  }

  addClassifierValue(value: CreateClassifierValueRequest): Observable<ClassifierValueByIdCard> {
    return this.http.post<ClassifierValueByIdCard>(`${environment.apiUrl}${API_CLASSIFIER_VALUES}`, value, {
      headers: this.headers
    });
  }

  editClassifierValue(value: UpdateClassifierValueRequest): Observable<ClassifierValueByIdCard> {
    return this.http.put<ClassifierValueByIdCard>(`${environment.apiUrl}${API_CLASSIFIER_VALUES}`, value, {
      headers: this.headers
    });
  }

  deleteClassifierValue(classifierId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_CLASSIFIER_VALUES}/${classifierId}`, {
      headers: this.headers
    });
  }

  // Structural unit endpoints
  getStructuralUnits(): Observable<StructuralUnitListItemPagedResponse> {
    return this.http.get<StructuralUnitListItemPagedResponse>(
      `${environment.apiUrl}${API_STRUCTURAL_UNIT}?pageSize=${GET_ALL_PAGE_SIZE}&sortBy.by=name&sortBy.desc=false`,
      {
        headers: this.headers
      }
    );
  }

  getStructuralUnitById(id: string): Observable<StructuralUnitCard> {
    return this.http.get<StructuralUnitCard>(`${environment.apiUrl}${API_STRUCTURAL_UNIT}/${id}`, { headers: this.headers });
  }

  getStructuralUnitOptions(): Observable<StructuralUnitMinimalListItem[]> {
    return this.http
      .get<
        StructuralUnitMinimalListItem[]
      >(`${environment.apiUrl}${API_STRUCTURAL_UNIT}/minimal-list?sortBy.by=name&sortBy.desc=false`, { headers: this.headers })
      .pipe(map((items) => [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'lv'))));
  }

  editStructuralUnit(id: string, args: UpdateStructuralUnitRequest): Observable<StructuralUnitCard> {
    return this.http.put<StructuralUnitCard>(`${environment.apiUrl}${API_STRUCTURAL_UNIT}/${id}`, args, {
      headers: this.headers
    });
  }

  deleteStructuralUnit(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_STRUCTURAL_UNIT}/${id}`, {
      headers: this.headers
    });
  }

  // Table data endpoint
  getTableData(endpoint: string, params: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}${endpoint}?${params}`);
  }

  // Reading room submission note endpoints
  getReadingRoomSubmissionNotesByIdList(id: string): Observable<ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto[]> {
    // const params = '?sortBy.by=name&sortBy.desc=false';
    return this.http.get<ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto[]>(
      `${environment.apiUrl}${API_READING_ROOM_SUBMISSION_NOTE_READING_ROOM_SUBMISSION}/${id}`,
      {
        headers: this.headers
      }
    );
  }

  createReadingRoomSubmissionNote(note: CreateReadingRoomSubmissionNoteRequestDto): Observable<ReadingRoomSubmissionNoteCardDto> {
    return this.http.post<ReadingRoomSubmissionNoteCardDto>(`${environment.apiUrl}${API_READING_ROOM_SUBMISSION_NOTE}`, note, {
      headers: this.headers
    });
  }

  updateReadingRoomSubmissionNote(note: UpdateReadingRoomSubmissionNoteRequestDto): Observable<ReadingRoomSubmissionNoteCardDto> {
    return this.http.put<ReadingRoomSubmissionNoteCardDto>(`${environment.apiUrl}${API_READING_ROOM_SUBMISSION_NOTE}`, note, {
      headers: this.headers
    });
  }

  deleteReadingRoomSubmissionNote(noteId: string) {
    return this.http.delete<void>(`${environment.apiUrl}${API_READING_ROOM_SUBMISSION_NOTE}/${noteId}`, { headers: this.headers });
  }

  // Reading room endpoints
  getReadingRoomList(): Observable<ReadingRoomListItemPagedResponse> {
    const params = '?sortBy.by=name&sortBy.desc=false&pageSize=1000';
    return this.http.get<ReadingRoomListItemPagedResponse>(`${environment.apiUrl}${API_READING_ROOM}/${params}`, {
      headers: this.headers
    });
  }

  getReadingRoomMinimalList(): Observable<ReadingRoomMinimalListItem[]> {
    const params = '?sortBy.by=name&sortBy.desc=false&pageSize=1000';
    return this.http.get<ReadingRoomMinimalListItem[]>(`${environment.apiUrl}${API_READING_ROOM}/minimal-list/${params}`, {
      headers: this.headers
    });
  }

  getReadingRoom(id: string): Observable<ReadingRoomCard> {
    return this.http.get<ReadingRoomCard>(`${environment.apiUrl}${API_READING_ROOM}/${id}`, { headers: this.headers });
  }

  createReadingRoom(readingRoom: CreateReadingRoomRequest): Observable<ReadingRoomCard> {
    return this.http.post<ReadingRoomCard>(`${environment.apiUrl}${API_READING_ROOM}`, readingRoom, {
      headers: this.headers
    });
  }

  updateReadingRoom(readingRoom: UpdateReadingRoomRequest): Observable<ReadingRoomCard> {
    return this.http.put<ReadingRoomCard>(`${environment.apiUrl}${API_READING_ROOM}`, readingRoom, {
      headers: this.headers
    });
  }

  deleteReadingRoom(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_READING_ROOM}?id=${id}`, { headers: this.headers });
  }

  getReadingRoomExcel(): Observable<any> {
    const params = window.location.search;

    return this.http.get(`${environment.apiUrl}${API_READING_ROOM_EXCEL}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  getReadingRoomApplicationDocumentExcel(applicationId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}${API_VISIT_ROOM_DOCUMENT_EXCEL}?readingRoomSubmissionId=${applicationId}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  getReadingRoomSubmissionExcel(myApps: boolean = false, structuralUnitIds: string[] | null = null): Observable<any> {
    let params = `${window.location.search}`;
    if (myApps) {
      if (params.length > 0) {
        params += `&filter.responsibleUserId=${this.authService.getUserId()}&filter.attendanceStatuses=Unknown`;
      } else {
        params += `?filter.responsibleUserId=${this.authService.getUserId()}&filter.attendanceStatuses=Unknown&`;
      }
    }

    if (structuralUnitIds) {
      for (const structuralUnitId of structuralUnitIds) {
        if (params.length > 0) {
          params += `&filter.structuralUnitIds=${structuralUnitId}`;
        } else {
          params += `?filter.structuralUnitIds=${structuralUnitId}`;
        }
      }
    }

    return this.http.get(`${environment.apiUrl}${API_VISIT_ROOM_SUBMISSION_EXCEL}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  getVisitList(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}${API_VISIT_LIST}`, {
      headers: this.headers
    });
  }

  initVisitRoomApplication(visitRoom: InitializeBackofficeReadingRoomSubmissionRequest): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.post<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM_INITIALIZE}`, visitRoom, {
      headers: this.headers
    });
  }

  deleteReadingRoomSubmission(applicationId: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}${API_VISIT_ROOM}/${applicationId}`, {
      headers: this.headers
    });
  }

  updateVisitRoomInfoDraft(visitRoom: UpdateDraftReadingRoomSubmissionRequest): Observable<BackofficeReadingRoomSubmissionCard> {
    const args = {
      ...visitRoom,
      researchTopicIds: [...new Set(visitRoom.researchTopicIds)]
    };

    return this.http.put<any>(`${environment.apiUrl}${API_VISIT_ROOM_INFO_DRAFT}`, args, { headers: this.headers });
  }

  updateVisitRoomTopics(topics: UpdateDraftReadingRoomSubmissionResearchTopicsRequest): Observable<any> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM_SET_TOPIC}`, topics, {
      headers: this.headers
    });
  }

  updateSetRestrictedAccessJustificationRequest(
    data: SetRestrictedAccessJustificationRequest
  ): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM_RESTRICTED_ACCESS_JUSTIFICATION}`,
      data,
      {
        headers: this.headers
      }
    );
  }

  getReadingRoomSubmission(id: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.get<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM}/${id}`, {
      headers: this.headers
    });
  }

  getReadingRoomSubmissionChanges(id: string): Observable<ReadingRoomSubmissionChangeListItem[]> {
    return this.http.get<ReadingRoomSubmissionChangeListItem[]>(`${environment.apiUrl}${API_VISIT_ROOM_CHANGES.replace('{id}', id)}`, {
      headers: this.headers
    });
  }

  downloadAuditExcel(): Observable<HttpResponse<Blob>> {
    // LNA-2053. The export takes every filtered row up to the backend's cap, so the list's paging
    // must not ride along: forwarding it exported only the page on screen (page=4&pageSize=50 gave
    // rows 151-200). Filters and sort are kept.
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('pageSize');

    if (!params.has('sortBy.by')) {
      params.set('sortBy.by', 'createdOn');
      params.set('sortBy.desc', 'true');
    }

    return this.http.get(`${environment.apiUrl}/api/audit/export/excel?${params.toString()}`, {
      headers: this.headers,
      observe: 'response',
      responseType: 'blob'
    });
  }

  getClientReadingRoomSubmissions(clientId: string): Observable<BackofficeReadingRoomSubmissionListItemPagedResponse> {
    return this.http.get<BackofficeReadingRoomSubmissionListItemPagedResponse>(
      `${environment.apiUrl}${API_VISIT_ROOM}?filters=clientId==${clientId}`,
      { headers: this.headers }
    );
  }

  updateVisitRoomDocumentOrder(documentOrder: UpdateDraftReadingRoomSubmissionRequestedDocumentsInformationRequest): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}${API_VISIT_ROOM_SET_DOCS}`, documentOrder, {
      headers: this.headers
    });
  }

  visitRoomAcceptTerms(applicationId: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM_AGREE_TO_TERMS}/${applicationId}`, {
      headers: this.headers
    });
  }

  updateVisitRoomDocumentBackofficeNotes(
    documentNotes: UpdateRequestedDocumentBackofficeNotesRequest
  ): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM_DOCUMENT_BACKOFFICE_NOTES}`,
      documentNotes,
      {
        headers: this.headers
      }
    );
  }

  submitVisitRoomApplication(applicationId: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM_SUBMIT}/${applicationId}`,
      {},
      { headers: this.headers }
    );
  }

  redirectSubmission(args: RedirectReadingRoomSubmissionRequest): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM}/redirect`, args, {
      headers: this.headers
    });
  }

  getSubmissionCommunicationList(applicationId: string): Observable<ReadingRoomSubmissionCommunicationListItemDtoPagedResult> {
    return this.http.get<ReadingRoomSubmissionCommunicationListItemDtoPagedResult>(
      `${environment.apiUrl}${API_VISIT_ROOM_SUBMISSION_COMMUNICATION}/${applicationId}?sortBy.by=id&sortBy.desc=true&pageSize=${GET_ALL_PAGE_SIZE}`,
      { headers: this.headers }
    );
  }

  createApplicationInfoRequest(
    request: CreateReadingRoomSubmissionRequestAdditionalInfoDto
  ): Observable<ReadingRoomSubmissionCommunicationDto> {
    return this.http.post<ReadingRoomSubmissionCommunicationDto>(`${environment.apiUrl}${API_VISIT_ROOM}/request-info`, request, {
      headers: this.headers
    });
  }

  rescheduleApplicationRequest(request: RescheduleReadingRoomSubmissionRequest): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM}/reschedule`, request, {
      headers: this.headers
    });
  }

  createAppClientNote(request: CreateReadingRoomSubmissionLogAdditionalInfoDto): Observable<ReadingRoomSubmissionCommunicationDto> {
    return this.http.post<ReadingRoomSubmissionCommunicationDto>(`${environment.apiUrl}${API_VISIT_ROOM}/log-info`, request, {
      headers: this.headers
    });
  }

  // Role endpoints
  getSystemRoles(): Observable<RoleListItemPagedResponse> {
    return this.http.get<RoleListItemPagedResponse>(`${environment.apiUrl}${API_ROLE}`, {
      headers: this.headers
    });
  }

  getStructuralUnitRoles(): Observable<StructuralUnitRoleListItemPagedResponse> {
    return this.http.get<StructuralUnitRoleListItemPagedResponse>(`${environment.apiUrl}${API_ROLE_STRUCTURAL_UNIT}`, {
      headers: this.headers
    });
  }

  getRole(id: number): Observable<RoleCard> {
    return this.http.get<RoleCard>(`${environment.apiUrl}${API_ROLE}/${id}`, {
      headers: this.headers
    });
  }

  updateRole(role: UpdateRoleRequest): Observable<RoleCard> {
    return this.http.put<RoleCard>(`${environment.apiUrl}${API_ROLE}`, role, {
      headers: this.headers
    });
  }

  // User endpoints
  getUser(id: string): Observable<UserCard> {
    return this.http.get<UserCard>(`${environment.apiUrl}${API_BACKOFFICE_USER}/${id}`, { headers: this.headers });
  }

  getCurrentUser(): Observable<UserCard> {
    return this.http.get<UserCard>(`${environment.apiUrl}${API_BACKOFFICE_USER}/me`, { headers: this.headers });
  }

  updateUser(user: UpdateUserRequest): Observable<UserCard> {
    return this.http.put<UserCard>(`${environment.apiUrl}${API_BACKOFFICE_USER}`, user, { headers: this.headers });
  }

  getAllUsers(): Observable<UserListItemPagedResponse> {
    return this.http.get<UserListItemPagedResponse>(`${environment.apiUrl}${API_BACKOFFICE_USER}?pageSize=${GET_ALL_PAGE_SIZE}`, {
      headers: this.headers
    });
  }

  getMinimalUsersList(
    searchValue: string = '',
    activationStatus?: string,
    showWithActiveAbsence?: boolean,
    structuralUnitId?: string
  ): Observable<UserMinimalListItem[]> {
    let filter =
      activationStatus?.length && showWithActiveAbsence !== null && showWithActiveAbsence !== undefined
        ? `&activationStatus=${activationStatus}&showWithActiveAbsence=${showWithActiveAbsence}`
        : '';
    if (structuralUnitId) {
      filter += `&structuralUnitId=${structuralUnitId}`;
    }

    return this.http.get<UserMinimalListItem[]>(
      `${environment.apiUrl}${API_BACKOFFICE_USER}/minimal-list?fullName=${searchValue}` + filter,
      {
        headers: this.headers
      }
    );
  }

  getAllAbsencesByUserId(userId: string): Observable<UserAbsenceListItem[]> {
    return this.http.get<UserAbsenceListItem[]>(`${environment.apiUrl}${API_ABSENCES}/user/${userId}?pageSize=${GET_ALL_PAGE_SIZE}`, {
      headers: this.headers
    });
  }

  deleteUserAbsence(absenceId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_ABSENCES}/absence/${absenceId}`, {
      headers: this.headers
    });
  }

  updateUserAbsence(absence: UpdateUserAbsenceRequest): Observable<UserAbsenceCard> {
    return this.http.put<UserAbsenceCard>(`${environment.apiUrl}${API_ABSENCES}`, absence, { headers: this.headers });
  }

  createUserAbsence(absence: CreateUserAbsenceRequest): Observable<UserAbsenceCard> {
    return this.http.post<UserAbsenceCard>(`${environment.apiUrl}${API_ABSENCES}`, absence, { headers: this.headers });
  }

  getUserExcel(): Observable<any> {
    const params = window.location.search;

    return this.http.get(`${environment.apiUrl}${API_USER_EXCEL}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  // Country endpoints
  getCountries(): Observable<CountryDtoPagedResult> {
    return this.http.get<CountryDtoPagedResult>(
      `${environment.apiUrl}${API_COUNTRY}?pageSize=${GET_ALL_PAGE_SIZE}&sortBy.by=name&sortBy.desc=false`,
      { headers: this.headers }
    );
  }

  // Client endpoints
  deleteClient(deleteData: DeleteClientRequest) {
    return this.http.delete<void>(`${environment.apiUrl}${API_CLIENT}`, {
      headers: this.headers,
      body: deleteData
    });
  }

  createClient(client: CreateClientRequest): Observable<ClientCard> {
    return this.http.post<ClientCard>(`${environment.apiUrl}${API_CLIENT}`, client, { headers: this.headers });
  }

  createLegalEntity(legalEntity: CreateLegalEntityRequest): Observable<LegalEntityCard> {
    return this.http.post<LegalEntityCard>(`${environment.apiUrl}${API_LEGAL_ENTITY}`, legalEntity, { headers: this.headers });
  }

  getLegalEntity(id: string): Observable<LegalEntityCard> {
    return this.http.get<LegalEntityCard>(`${environment.apiUrl}${API_LEGAL_ENTITY}/${id}`, { headers: this.headers });
  }

  updateLegalEntity(legalEntity: UpdateLegalEntityRequest): Observable<LegalEntityCard> {
    return this.http.put<LegalEntityCard>(`${environment.apiUrl}${API_LEGAL_ENTITY}`, legalEntity, { headers: this.headers });
  }

  getLegalEntityForms(): Observable<LegalEntityFormOption[]> {
    return this.http.get<LegalEntityFormOption[]>(`${environment.apiUrl}${API_LEGAL_ENTITY_FORMS}`, { headers: this.headers });
  }

  getClient(id: string): Observable<ClientCard> {
    return this.http.get<ClientCard>(`${environment.apiUrl}${API_CLIENT}/${id}`, { headers: this.headers });
  }

  getClientsForInput(searchValue: string, status: string, clientType?: number): Observable<ClientListItemPagedResponse> {
    let params = `?filter.searchText=${searchValue}&filter.status=${status}&page=1&pageSize=${GET_ALL_PAGE_SIZE}`;
    if (clientType !== undefined) {
      const clientTypeString = clientType === 2 ? 'Organization' : 'Individual';
      params += `&filter.clientType=${clientTypeString}`;
    }
    return this.http.get<ClientListItemPagedResponse>(`${environment.apiUrl}${API_CLIENT}${params}`, {
      headers: this.headers
    });
  }

  getClientByLegalEntityId(legalEntityId: string): Observable<ClientListItemPagedResponse> {
    const params = `?filter.legalEntityId=${legalEntityId}&page=1&pageSize=1`;
    return this.http.get<ClientListItemPagedResponse>(`${environment.apiUrl}${API_CLIENT}${params}`, {
      headers: this.headers
    });
  }

  getLegalEntityContactInfo(legalEntityId: string): Observable<LegalEntityContactInfo> {
    return this.http.get<LegalEntityContactInfo>(`${environment.apiUrl}${API_LEGAL_ENTITY}/${legalEntityId}/contact-info`, {
      headers: this.headers
    });
  }

  getLegalEntitiesForInput(searchValue: string): Observable<LegalEntityListItemPagedResponse> {
    const params = `?filter.searchText=${searchValue}&page=1&pageSize=${GET_ALL_PAGE_SIZE}`;
    return this.http.get<LegalEntityListItemPagedResponse>(`${environment.apiUrl}${API_LEGAL_ENTITY}${params}`, {
      headers: this.headers
    });
  }

  getUsersForInput(searchValue: string): Observable<UserListItemPagedResponse> {
    const params = `?filter.searchText=${searchValue}&sortBy.by=fullName&sortBy.desc=false&filter.hasActiveAbsence=false&filter.activationStatus=Active&page=1&pageSize=${GET_ALL_PAGE_SIZE}`;
    return this.http.get<UserListItemPagedResponse>(`${environment.apiUrl}${API_BACKOFFICE_USER}${params}`, {
      headers: this.headers
    });
  }

  getClientExcel(): Observable<any> {
    const params = window.location.search;

    return this.http.get(`${environment.apiUrl}${API_CLIENT}/export/excel${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  getOrganizationExcel(): Observable<any> {
    const params = window.location.search;

    return this.http.get(`${environment.apiUrl}${API_LEGAL_ENTITY}/export/excel${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  updateClient(client: UpdateClientRequest): Observable<ClientCard> {
    return this.http.put<ClientCard>(`${environment.apiUrl}${API_CLIENT}`, client, { headers: this.headers });
  }

  resendClientActivationEmail(clientId: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}${API_CLIENT}/${clientId}/resend-activation-email`,
      { id: clientId },
      { headers: this.headers }
    );
  }

  // Client notification endpoints
  getClientNotifications(
    clientId: string,
    page: number = 1,
    pageSize: number = 10
  ): Observable<ClientNotificationByClientIdListItemPagedResponse> {
    const params = `?sortBy.by=createdOn&sortBy.desc=true&page=${page}&pageSize=${pageSize}`;

    return this.http.get<ClientNotificationByClientIdListItemPagedResponse>(
      `${environment.apiUrl}${API_CLIENT_NOTIFICATIONS}/${clientId}${params}`,
      { headers: this.headers }
    );
  }

  getNotificationById(notificationId: string): Observable<ClientNotificationCard> {
    return this.http.get<ClientNotificationCard>(`${environment.apiUrl}${API_CLIENT_NOTIFICATIONS}/${notificationId}`, {
      headers: this.headers
    });
  }

  createClientNotification(data: CreateClientNotificationRequest): Observable<ClientNotificationCard> {
    return this.http.post<ClientNotificationCard>(`${environment.apiUrl}${API_CLIENT_NOTIFICATIONS}`, data, {
      headers: this.headers
    });
  }

  updateClientNotification(data: UpdateClientNotificationRequest): Observable<ClientNotificationCard> {
    return this.http.put<ClientNotificationCard>(`${environment.apiUrl}${API_CLIENT_NOTIFICATIONS}`, data, {
      headers: this.headers
    });
  }

  markAsReadClientNotification(data: ReadClientNotificationRequest): Observable<ClientNotificationCard> {
    return this.http.put<ClientNotificationCard>(`${environment.apiUrl}${API_CLIENT_NOTIFICATIONS}/read`, data, {
      headers: this.headers
    });
  }

  // Client login endpoints
  getClientLoginList(clientId: string): Observable<ClientLoginListItem[]> {
    return this.http.get<ClientLoginListItem[]>(`${environment.apiUrl}${API_CLIENT_LOGIN_LIST}/client/${clientId}`, {
      headers: this.headers
    });
  }

  getLegalEntityLoginList(legalEntityId: string): Observable<ClientLoginListByLegalEntityIdItem[]> {
    return this.http.get<ClientLoginListByLegalEntityIdItem[]>(
      `${environment.apiUrl}${API_CLIENT_LOGIN_LIST}/legalentity/${legalEntityId}`,
      { headers: this.headers }
    );
  }

  // Client notes endpoints
  getClientNote(noteId: number): Observable<ClientNoteCard> {
    return this.http.get<ClientNoteCard>(`${environment.apiUrl}${API_CLIENT_NOTE}/${noteId}`, {
      headers: this.headers
    });
  }

  createClientNote(note: CreateClientNoteRequest): Observable<ClientNoteCard> {
    return this.http.post<ClientNoteCard>(`${environment.apiUrl}${API_CLIENT_NOTE}`, note, { headers: this.headers });
  }

  updateClientNote(note: UpdateClientNoteRequest): Observable<ClientNoteCard> {
    return this.http.put<ClientNoteCard>(`${environment.apiUrl}${API_CLIENT_NOTE}`, note, { headers: this.headers });
  }

  deleteClientNote(noteId: number) {
    return this.http.delete<void>(`${environment.apiUrl}${API_CLIENT_NOTE}/${noteId}`, { headers: this.headers });
  }

  getAllClientNotes(
    clientId: string,
    page: number = 1,
    pageSize: number = DEFAULT_NOTES_PAGE_SIZE
  ): Observable<ClientNoteListByClientIdItem[]> {
    return this.http.get<ClientNoteListByClientIdItem[]>(
      `${environment.apiUrl}${API_CLIENT_NOTE}/client/${clientId}?sorts=id&page=${page}&pageSize=${pageSize}`,
      { headers: this.headers }
    );
  }

  // Legal entity notes endpoints
  getLegalEntityNote(noteId: string): Observable<LegalEntityNoteCard> {
    return this.http.get<LegalEntityNoteCard>(`${environment.apiUrl}${API_LEGAL_ENTITY_NOTE}/${noteId}`, {
      headers: this.headers
    });
  }

  createLegalEntityNote(note: CreateLegalEntityNoteRequest): Observable<LegalEntityNoteCard> {
    return this.http.post<LegalEntityNoteCard>(`${environment.apiUrl}${API_LEGAL_ENTITY_NOTE}`, note, { headers: this.headers });
  }

  updateLegalEntityNote(note: UpdateLegalEntityNoteRequest): Observable<LegalEntityNoteCard> {
    return this.http.put<LegalEntityNoteCard>(`${environment.apiUrl}${API_LEGAL_ENTITY_NOTE}`, note, { headers: this.headers });
  }

  deleteLegalEntityNote(noteId: string) {
    return this.http.delete<void>(`${environment.apiUrl}${API_LEGAL_ENTITY_NOTE}/${noteId}`, { headers: this.headers });
  }

  getAllLegalEntityNotes(
    legalEntityId: string,
    page: number = 1,
    pageSize: number = DEFAULT_NOTES_PAGE_SIZE
  ): Observable<LegalEntityNoteListItem[]> {
    return this.http.get<LegalEntityNoteListItem[]>(
      `${environment.apiUrl}${API_LEGAL_ENTITY_NOTE}/legalentity/${legalEntityId}?sorts=id&page=${page}&pageSize=${pageSize}`,
      { headers: this.headers }
    );
  }

  // Reader card endpoints
  getReadersCard(clientId: string): Observable<ReaderCardByClientId> {
    return this.http.get<ReaderCardByClientId>(`${environment.apiUrl}${API_CLIENT_READERS_CARD}/${clientId}`, {
      headers: this.headers
    });
  }

  createReadersCard(readersCard: CreateReaderCardRequest): Observable<ReaderCardById> {
    return this.http.post<ReaderCardById>(`${environment.apiUrl}${API_READERS_CARD}`, readersCard, {
      headers: this.headers
    });
  }

  updateReadersCard(readersCard: UpdateReaderCardRequest): Observable<ReaderCardById> {
    return this.http.put<ReaderCardById>(`${environment.apiUrl}${API_READERS_CARD}`, readersCard, {
      headers: this.headers
    });
  }

  // Research topic endpoints
  deleteResearchTopic(id: string): Observable<any> {
    const params = `?id=${id}`;
    return this.http.delete(`${environment.apiUrl}${API_RESEARCH_TOPIC}${params}`, {
      headers: this.headers
    });
  }

  getResearchTopicById(id: string): Observable<ResearchTopicByIdCard> {
    return this.http.get<ResearchTopicByIdCard>(`${environment.apiUrl}${API_RESEARCH_TOPIC}/${id}`, {
      headers: this.headers
    });
  }

  getResearchTopics(): Observable<ResearchTopicListItemPagedResponse> {
    return this.http.get<ResearchTopicListItemPagedResponse>(`${environment.apiUrl}${API_RESEARCH_TOPIC}?pageSize=${GET_ALL_PAGE_SIZE}`, {
      headers: this.headers
    });
  }

  getClientResearchTopics(clientId: string): Observable<ResearchTopicByClientIdCardPagedResponse> {
    return this.http.get<ResearchTopicByClientIdCardPagedResponse>(
      `${environment.apiUrl}${API_RESEARCH_TOPIC}/client/${clientId}?pageSize=${GET_ALL_PAGE_SIZE}&sortBy.by=id&sortBy.desc=true`,
      { headers: this.headers }
    );
  }

  createResearchTopic(researchTopic: CreateResearchTopicRequest): Observable<ResearchTopicByIdCard> {
    return this.http.post<ResearchTopicByIdCard>(`${environment.apiUrl}${API_RESEARCH_TOPIC}`, researchTopic, {
      headers: this.headers
    });
  }

  updateResearchTopic(researchTopic: UpdateResearchTopicRequest): Observable<ResearchTopicByIdCard> {
    return this.http.put<ResearchTopicByIdCard>(`${environment.apiUrl}${API_RESEARCH_TOPIC}`, researchTopic, {
      headers: this.headers
    });
  }

  updateResearchTopicActivity(args: UpdateResearchTopicActivityRequest): Observable<ResearchTopicByIdCard> {
    return this.http.put<ResearchTopicByIdCard>(`${environment.apiUrl}${API_RESEARCH_TOPIC}/update-activity`, args, {
      headers: this.headers
    });
  }

  // File endpoints
  downloadFile(fileId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}${API_DOWNLOAD_FILE}/${fileId}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  uploadFile(
    file: File,
    fileObjectType: string,
    objectId: string,
    note: string = '',
    termType: string = FILE_TERM_TYPE_UNKNOWN,
    validTill: string | null = null,
    backofficeNote: string = ''
  ): Observable<any> {
    const formData = new FormData();
    formData.append('data', file);
    formData.append('objectType', fileObjectType);
    formData.append('objectId', objectId.toString());
    formData.append('fileNote.note', note);
    formData.append('fileNote.termType', termType);
    if (validTill && termType === FILE_TERM_TYPE_FIXED) {
      formData.append('fileNote.validTill', validTill);
    }
    if (backofficeNote.length) {
      formData.append('fileNote.backofficeNote', backofficeNote);
    }
    return this.http.post(`${environment.apiUrl}${API_UPLOAD_FILE}`, formData, {
      headers: this.headers
    });
  }

  deleteFile(fileId: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}${API_FILE}?id=${fileId}`, {});
  }

  updateFile(args: UpdateFileNoteRequest): Observable<any> {
    return this.http.put(`${environment.apiUrl}${API_FILE}`, args, {});
  }

  // User notification endpoints
  getAllUserNotifications(userId: string): Observable<UserNotificationListByUserIdItemPagedResponse> {
    return this.http.get<UserNotificationListByUserIdItemPagedResponse>(
      `${environment.apiUrl}${API_USER_NOTIFICATION}/user/${userId}?pageSize=${GET_ALL_PAGE_SIZE}`,
      { headers: this.headers }
    );
  }

  getUserNotificationsExcel(userId: string): Observable<any> {
    const params = window.location.search;

    return this.http.get(`${environment.apiUrl}${API_USER_NOTIFICATION}/export/excel/${userId}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  createUserNotification(notificationData: CreateUserNotificationRequest) {
    return this.http.post<CreateUserNotificationRequest>(`${environment.apiUrl}${API_USER_NOTIFICATION}`, notificationData, {
      headers: this.headers
    });
  }

  updateUserNotification(notificationData: UpdateUserNotificationRequest) {
    return this.http.put<UpdateUserNotificationRequest>(`${environment.apiUrl}${API_RESEARCH_TOPIC}`, notificationData, {
      headers: this.headers
    });
  }

  getUserNotification(notificationId: number): Observable<UserNotificationCard> {
    return this.http.get<UserNotificationCard>(`${environment.apiUrl}${API_USER_NOTIFICATION}/${notificationId}`, {
      headers: this.headers
    });
  }

  markAsReadUserNotification(notificationId: string, isRead: boolean = true) {
    const request$ = this.http.put<UpdateUserNotificationIsReadRequest>(
      `${environment.apiUrl}/api/user-notification/read`,
      { id: notificationId, isRead: isRead },
      { headers: this.headers }
    );

    // TODO: FIX THIS, Incorrect types

    // request$.subscribe((updatedNotification) => {
    //   this.notificationsReadChangeSubject.next(updatedNotification);
    // });

    return request$;
  }

  // Service processing endpoints
  startProcessing(readingRoomSubmissionId: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM}/process/${readingRoomSubmissionId}`,
      {},
      { headers: this.headers }
    );
  }

  rejectSubmission(readingRoomSubmissionId: string, rejectionReason: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM}/reject`,
      {
        readingRoomSubmissionId: readingRoomSubmissionId,
        rejectionReason: rejectionReason
      },
      { headers: this.headers }
    );
  }

  cancelSubmission(readingRoomSubmissionId: string, cancellationReason: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM}/cancel`,
      {
        readingRoomSubmissionId: readingRoomSubmissionId,
        cancellationReason: cancellationReason
      },
      { headers: this.headers }
    );
  }

  approveSubmission(readingRoomSubmissionId: string, informationForClient: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM}/approve`,
      {
        readingRoomSubmissionId: readingRoomSubmissionId,
        informationForClient: informationForClient ?? null
      },
      { headers: this.headers }
    );
  }

  attendStatusReadingRoomSumbission(data: AttendReadingRoomSubmissionRequestDto): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM}/attend`, data, {
      headers: this.headers
    });
  }

  reassignReadingRoomSumbission(data: ReassignReadingRoomSubmissionRequest): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(`${environment.apiUrl}${API_VISIT_ROOM}/reassign`, data, {
      headers: this.headers
    });
  }

  markAsReadAppCommunication(communicationId: string): Observable<BackofficeReadingRoomSubmissionCard> {
    return this.http.put<BackofficeReadingRoomSubmissionCard>(
      `${environment.apiUrl}${API_VISIT_ROOM_SUBMISSION_COMMUNICATION_READ}/${communicationId}`,
      {},
      { headers: this.headers }
    );
  }

  updateLogAdditionalInformationForReadingRoomSubmission(
    data: UpdateLogAdditionalInformationForReadingRoomSubmissionRequest
  ): Observable<ReadingRoomSubmissionCommunicationDto> {
    return this.http.put<ReadingRoomSubmissionCommunicationDto>(
      `${environment.apiUrl}${API_READING_ROOM_SUBMISSION_COMMUNICATION_LOG_INFO}`,
      data,
      { headers: this.headers }
    );
  }

  getTooltip(id: string): Observable<TooltipListItem> {
    return this.http.get<TooltipListItem>(`${environment.apiUrl}${API_TOOLTIP}/${id}`, {
      headers: this.headers
    });
  }

  updateTooltip(id: string, tooltip: UpdateTooltipRequest): Observable<TooltipListItem> {
    return this.http.put<TooltipListItem>(`${environment.apiUrl}${API_TOOLTIP}`, tooltip, {
      headers: this.headers
    });
  }

  // Information Request
  getInformationRequestTopics(): Observable<InformationRequestTopicResponse[]> {
    return this.http.get<InformationRequestTopicResponse[]>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/topics`, {
      headers: this.headers
    });
  }

  getInformationRequestFormLabels(language: Language = 'Lv'): Observable<InformationRequestLabelsResponse> {
    return this.http.get<InformationRequestLabelsResponse>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/form-labels?language=${language}`,
      { headers: this.headers }
    );
  }

  getInformationRequest(id: string): Observable<InformationRequestCard> {
    return this.http.get<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}`, {
      headers: this.headers
    });
  }

  getIrDocuments(id: string): Observable<InformationRequestDocumentItem[]> {
    return this.http.get<InformationRequestDocumentItem[]>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/documents`,
      { headers: this.headers }
    );
  }

  updateIrDocumentDescription(id: string, outerId: string, description: string): Observable<string> {
    return this.http.put<string>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/documents/description`,
      { outerId, description },
      { headers: this.headers }
    );
  }

  createInformationRequest(args: CreateBoInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}`, args, {
      headers: this.headers
    });
  }

  updateInformationRequest(id: string, args: UpdateBoInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.put<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}`, args, {
      headers: this.headers
    });
  }

  submitInformationRequest(id: string): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/submit`, null, {
      headers: this.headers
    });
  }

  deleteInformationRequest(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}`, {
      headers: this.headers
    });
  }

  getInformationRequestExcel(
    currentQueryParams: URLSearchParams,
    defaultFilters: { [key: string]: string | string[] } = {},
    topFilterParamName: string = '',
    topFilterSelected: string[] = [],
    initialColumnFilters: { [paramName: string]: string[] } = {}
  ): Observable<any> {
    const params = this.buildExportParams(currentQueryParams, defaultFilters, topFilterParamName, topFilterSelected, initialColumnFilters);
    return this.http.get(`${environment.apiUrl}${API_INFORMATION_REQUEST_EXCEL}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  getInformationRequestTaskExcel(
    currentQueryParams: URLSearchParams,
    defaultFilters: { [key: string]: string | string[] } = {},
    topFilterParamName: string = '',
    topFilterSelected: string[] = [],
    initialColumnFilters: { [paramName: string]: string[] } = {}
  ): Observable<any> {
    const params = this.buildExportParams(currentQueryParams, defaultFilters, topFilterParamName, topFilterSelected, initialColumnFilters);
    return this.http.get(`${environment.apiUrl}${API_INFORMATION_REQUEST_TASK_EXCEL}${params}`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  private buildExportParams(
    currentQueryParams: URLSearchParams,
    defaultFilters: { [key: string]: string | string[] },
    topFilterParamName: string,
    topFilterSelected: string[],
    initialColumnFilters: { [paramName: string]: string[] }
  ): string {
    const params = new URLSearchParams(currentQueryParams);
    if (topFilterParamName) {
      params.delete(`filter.${topFilterParamName}`);
    }
    params.delete('page');
    params.delete('pageSize');
    Object.entries(initialColumnFilters).forEach(([paramName, values]) => {
      const key = `filter.${paramName}`;
      if (params.has(key)) return;
      values.forEach((v) => {
        if (v) {
          params.append(key, v);
        }
      });
    });
    Object.entries(defaultFilters).forEach(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      values.forEach((v) => {
        if (v !== null && v !== undefined && v !== '') {
          params.append(`filter.${key}`, String(v));
        }
      });
    });
    if (topFilterParamName) {
      topFilterSelected.forEach((v) => {
        params.append(`filter.${topFilterParamName}`, v);
      });
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  startPreliminaryResearch(id: string, structuralUnitId?: string | null): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/start-preliminary-research`,
      { structuralUnitId },
      { headers: this.headers }
    );
  }

  startInfoRequestProcessing(id: string, mustCompleteByDate: string): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/start-processing`,
      { mustCompleteByDate },
      { headers: this.headers }
    );
  }

  completeInformationRequest(id: string, args: CompleteInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/complete`, args, {
      headers: this.headers
    });
  }

  cancelInformationRequest(id: string, args: CancelBoInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/cancel`, args, {
      headers: this.headers
    });
  }

  // Response document types
  setResponseDocumentTypes(id: string, items: ResponseDocumentTypeItemRequest[]): Observable<InformationRequestCard> {
    return this.http.put<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/response-document-types`,
      { items },
      { headers: this.headers }
    );
  }

  // Response drafts (Atbildes sagatave)
  createResponseDraft(id: string, text: string): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/response-drafts`,
      { text },
      { headers: this.headers }
    );
  }

  updateResponseDraft(id: string, draftId: string, text: string): Observable<InformationRequestCard> {
    return this.http.put<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/response-drafts/${draftId}`,
      { text },
      { headers: this.headers }
    );
  }

  deleteResponseDraft(id: string, draftId: string): Observable<InformationRequestCard> {
    return this.http.delete<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/response-drafts/${draftId}`, {
      headers: this.headers
    });
  }

  // Other responses (Cita atbilde)
  createOtherResponse(id: string, description: string): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/other-responses`,
      { description },
      { headers: this.headers }
    );
  }

  updateOtherResponse(id: string, otherId: string, description: string): Observable<InformationRequestCard> {
    return this.http.put<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/other-responses/${otherId}`,
      { description },
      { headers: this.headers }
    );
  }

  deleteOtherResponse(id: string, otherId: string): Observable<InformationRequestCard> {
    return this.http.delete<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/other-responses/${otherId}`, {
      headers: this.headers
    });
  }

  // Namejs documents
  requestNamejsDocument(id: string, kind: NamejsDocumentKind): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/namejs-documents/${kind}`, null, {
      headers: this.headers
    });
  }

  retrieveNamejsDocument(id: string, kind: NamejsDocumentKind): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/namejs-documents/${kind}/retrieve`,
      null,
      { headers: this.headers }
    );
  }

  // Sending events
  recordSendingEvent(id: string, args: RecordSendingEventRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/sending-events`, args, {
      headers: this.headers
    });
  }

  // LNA-2050 #16: "Nosūtīt atbildi uz Portālu" has its own endpoint, like the invoice's send-to-portal.
  sendResponseToPortal(id: string, args: SendResponseToPortalRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/sending-events/send-to-portal`,
      args,
      { headers: this.headers }
    );
  }

  // === IR Payment types & Mark as paid ===

  updateIrPaymentTypes(id: string, args: UpdateInformationRequestPaymentTypesRequest): Observable<InformationRequestCard> {
    return this.http.put<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/payment-types`, args, {
      headers: this.headers
    });
  }

  markIrAsPaid(id: string, args: MarkInformationRequestAsPaidRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/mark-paid`, args, {
      headers: this.headers
    });
  }

  // === IR Calculations ===

  getIrCalculationsByRequestId(id: string): Observable<InformationRequestCalculationListByInformationRequestIdItem[]> {
    return this.http.get<InformationRequestCalculationListByInformationRequestIdItem[]>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST_CALCULATION}/information-request/${id}`,
      { headers: this.headers }
    );
  }

  createIrCalculation(args: CreateInformationRequestCalculationRequest): Observable<InformationRequestCalculationCard> {
    return this.http.post<InformationRequestCalculationCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_CALCULATION}`, args, {
      headers: this.headers
    });
  }

  updateIrCalculation(args: UpdateInformationRequestCalculationRequest): Observable<InformationRequestCalculationCard> {
    return this.http.put<InformationRequestCalculationCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_CALCULATION}`, args, {
      headers: this.headers
    });
  }

  deleteIrCalculation(calculationId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_INFORMATION_REQUEST_CALCULATION}/${calculationId}`, {
      headers: this.headers
    });
  }

  // === IR Invoices ===

  getIrInvoicesByRequestId(id: string): Observable<InformationRequestInvoiceListByInformationRequestIdItem[]> {
    return this.http.get<InformationRequestInvoiceListByInformationRequestIdItem[]>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}/information-request/${id}`,
      { headers: this.headers }
    );
  }

  createIrInvoice(args: CreateInformationRequestInvoiceRequest): Observable<InformationRequestInvoiceCard> {
    return this.http.post<InformationRequestInvoiceCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}`, args, {
      headers: this.headers
    });
  }

  updateIrInvoice(args: UpdateInformationRequestInvoiceRequest): Observable<InformationRequestInvoiceCard> {
    return this.http.put<InformationRequestInvoiceCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}`, args, {
      headers: this.headers
    });
  }

  deleteIrInvoice(invoiceId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}/${invoiceId}`, { headers: this.headers });
  }

  sendIrInvoiceByEmail(args: SendInformationRequestInvoiceByEmailRequest): Observable<InformationRequestInvoiceCard> {
    return this.http.post<InformationRequestInvoiceCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}/send-by-email`, args, {
      headers: this.headers
    });
  }

  sendIrInvoiceToPortal(args: SendInformationRequestInvoiceToPortalRequest): Observable<InformationRequestInvoiceCard> {
    return this.http.post<InformationRequestInvoiceCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}/send-to-portal`, args, {
      headers: this.headers
    });
  }

  markIrInvoiceSent(args: MarkInformationRequestInvoiceSentRequest): Observable<InformationRequestInvoiceCard> {
    return this.http.post<InformationRequestInvoiceCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_INVOICE}/mark-sent`, args, {
      headers: this.headers
    });
  }

  // === IR Forward / Hand-over ===

  forwardIrToStructuralUnit(id: string, args: ForwardInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/forward-to-structural-unit`,
      args,
      { headers: this.headers }
    );
  }

  handOverIrToEmployee(id: string, args: HandOverInformationRequestRequest): Observable<InformationRequestCard> {
    return this.http.post<InformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/hand-over-to-employee`, args, {
      headers: this.headers
    });
  }

  // === IR Notes ===

  getIrNotesByRequestId(id: string): Observable<InformationRequestNoteListByInformationRequestIdItem[]> {
    return this.http.get<InformationRequestNoteListByInformationRequestIdItem[]>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST_NOTE}/information-request/${id}`,
      { headers: this.headers }
    );
  }

  createIrNote(args: CreateInformationRequestNoteRequest): Observable<InformationRequestNoteCard> {
    return this.http.post<InformationRequestNoteCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_NOTE}`, args, {
      headers: this.headers
    });
  }

  updateIrNote(args: UpdateInformationRequestNoteRequest): Observable<InformationRequestNoteCard> {
    return this.http.put<InformationRequestNoteCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_NOTE}`, args, {
      headers: this.headers
    });
  }

  deleteIrNote(noteId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}${API_INFORMATION_REQUEST_NOTE}/${noteId}`, { headers: this.headers });
  }

  // === IR Communication ===

  getIrCommunicationList(informationRequestId: string): Observable<BackofficeInformationRequestCommunicationListItemPagedResponse> {
    return this.http.get<BackofficeInformationRequestCommunicationListItemPagedResponse>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/communication-list/${informationRequestId}?sortBy.by=id&sortBy.desc=true&pageSize=${GET_ALL_PAGE_SIZE}`,
      { headers: this.headers }
    );
  }

  getIrCommunication(id: string): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.get<BackofficeInformationRequestCommunicationCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/communication/${id}`,
      { headers: this.headers }
    );
  }

  createIrRequestInfo(
    args: RequestAdditionalInformationForInformationRequestRequest
  ): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.post<BackofficeInformationRequestCommunicationCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/request-info`,
      args,
      { headers: this.headers }
    );
  }

  createIrLogInfo(args: LogAdditionalInformationForInformationRequestRequest): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.post<BackofficeInformationRequestCommunicationCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/log-info`, args, {
      headers: this.headers
    });
  }

  updateIrLogInfo(
    args: UpdateLogAdditionalInformationForInformationRequestRequest
  ): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.put<BackofficeInformationRequestCommunicationCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/update-log-info`,
      args,
      { headers: this.headers }
    );
  }

  sendIrByEmail(args: SendByEmailForInformationRequestRequest): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.post<BackofficeInformationRequestCommunicationCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/send-by-email`,
      args,
      { headers: this.headers }
    );
  }

  sendResponseByEmail(args: SendResponseByEmailForInformationRequestRequest): Observable<string> {
    return this.http.post<string>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/send-response-by-email`, args, {
      headers: this.headers
    });
  }

  // === IR Tasks ===

  createIrTask(args: CreateInformationRequestTaskRequest): Observable<InformationRequestTaskCard> {
    return this.http.post<InformationRequestTaskCard>(`${environment.apiUrl}/api/information-request-task`, args, {
      headers: this.headers
    });
  }

  getIrTaskList(informationRequestId: string): Observable<InformationRequestTaskListResult> {
    return this.http.get<InformationRequestTaskListResult>(
      `${environment.apiUrl}/api/information-request-task?filter.informationRequestId=${informationRequestId}&filter.Scope=2&sortBy.by=executionDate&sortBy.desc=true&pageSize=${GET_ALL_PAGE_SIZE}`,
      { headers: this.headers }
    );
  }

  markIrCommunicationAsRead(communicationId: string): Observable<BackofficeInformationRequestCommunicationCard> {
    return this.http.put<BackofficeInformationRequestCommunicationCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/read-communication/${communicationId}`,
      null,
      { headers: this.headers }
    );
  }

  // Information Request Task
  acceptIrTask(taskId: string): Observable<InformationRequestTaskCard> {
    return this.http.post<InformationRequestTaskCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_TASK}/${taskId}/accept`, null, {
      headers: this.headers
    });
  }

  completeIrTask(taskId: string): Observable<InformationRequestTaskCard> {
    return this.http.post<InformationRequestTaskCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_TASK}/${taskId}/complete`, null, {
      headers: this.headers
    });
  }

  cancelIrTask(taskId: string, args: CancelInformationRequestTaskRequest): Observable<InformationRequestTaskCard> {
    return this.http.post<InformationRequestTaskCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST_TASK}/${taskId}/cancel`, args, {
      headers: this.headers
    });
  }

  addCoResponsibleUnit(irId: string, args: AddCoResponsibleStructuralUnitRequest): Observable<BackofficeInformationRequestCard> {
    return this.http.post<BackofficeInformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${irId}/co-responsible-units`,
      args,
      { headers: this.headers }
    );
  }

  removeCoResponsibleUnit(irId: string, structuralUnitId: string): Observable<BackofficeInformationRequestCard> {
    return this.http.delete<BackofficeInformationRequestCard>(
      `${environment.apiUrl}${API_INFORMATION_REQUEST}/${irId}/co-responsible-units/${structuralUnitId}`,
      { headers: this.headers }
    );
  }

  setConsolidated(irId: string, args: SetConsolidatedRequest): Observable<BackofficeInformationRequestCard> {
    return this.http.put<BackofficeInformationRequestCard>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${irId}/consolidated`, args, {
      headers: this.headers
    });
  }

  getInformationRequestChanges(id: string): Observable<InformationRequestChangeListItem[]> {
    return this.http.get<InformationRequestChangeListItem[]>(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/changes`, {
      headers: this.headers
    });
  }

  downloadInformationRequestPdf(id: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}${API_INFORMATION_REQUEST}/${id}/pdf`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }
}
